import React, { useMemo, useState } from 'react';
import {
    Alert,
    Button,
    Card,
    Checkbox,
    Col,
    DatePicker,
    Input,
    InputNumber,
    Modal,
    Row,
    Select,
    Space,
    Table,
    Tag,
    Typography,
    message,
} from 'antd';
import dayjs from 'dayjs';
import { getPhotoShootSessionDate } from '../../utils/photoShootSessionDate';
import {
    useDeleteRateCard,
    useMatcherFields,
    usePreviewRateCard,
    useRateCardList,
    useRateCardVersions,
    useSaveRateCard,
} from '../../hooks/useRateCards';

const { Title, Text } = Typography;
const BAY_TYPES = ['model', 'product', 'scanning'];
const OPS = ['eq', 'in', 'regex', 'exists', 'notExists'];

function errorText(error) {
    const body = error?.response?.data?.message;
    if (typeof body === 'string') return body;
    return 'Request failed';
}

function blankLine() {
    return { lineId: '', label: '', bayType: 'model', rate: 0, note: '', conditions: [] };
}

function blankCondition() {
    return { field: '', op: 'eq', value: '' };
}

function valueForForm(value) {
    return Array.isArray(value) ? value.join(', ') : value == null ? '' : String(value);
}

function valueForApi(op, value) {
    if (op === 'exists' || op === 'notExists') return undefined;
    if (op === 'in') {
        return String(value || '')
            .split(',')
            .map(item => item.trim())
            .filter(Boolean);
    }
    if (value === 'true') return true;
    if (value === 'false') return false;
    return value;
}

function linesForApi(lines) {
    return lines.map(line => ({
        lineId: line.lineId || undefined,
        label: line.label,
        bayType: line.bayType,
        rate: Number(line.rate) || 0,
        note: line.note || '',
        band: line.band || undefined,
        conditions: (line.conditions || [])
            .filter(condition => condition.field && condition.op)
            .map(condition => ({
                field: condition.field,
                op: condition.op,
                value: valueForApi(condition.op, condition.value),
            })),
    }));
}

function cloneLines(lines) {
    return (lines || []).map(line => ({
        lineId: line.lineId,
        label: line.label,
        bayType: line.bayType,
        rate: line.rate,
        note: line.note || '',
        band: line.band ? { ...line.band } : undefined,
        conditions: (line.conditions || []).map(condition => ({
            field: condition.field,
            op: condition.op,
            value: valueForForm(condition.value),
        })),
    }));
}

function bandText(band) {
    if (!band) return '';
    const parts = [`up to ${band.max}`];
    if (band.add) parts.push(`+${band.add} extra`);
    if (band.luxury === false) parts.push('no luxury');
    else if (band.luxuryRate != null) parts.push(`luxury ${band.luxuryRate}`);
    if (band.plus) parts.push(`+${band.plus}`);
    return parts.join(', ');
}

export default function RateCards() {
    const studioToday = getPhotoShootSessionDate();
    const { data: cards, isFetching, error } = useRateCardList();
    const [search, setSearch] = useState('');
    const [companyCode, setCompanyCode] = useState('');
    const [editing, setEditing] = useState(false);
    const [effectiveFrom, setEffectiveFrom] = useState(studioToday);
    const [logisticsPct, setLogisticsPct] = useState(0);
    const [note, setNote] = useState('');
    const [allowBackdate, setAllowBackdate] = useState(false);
    const [scope, setScope] = useState('bay');
    const [unmatchedRate, setUnmatchedRate] = useState(0);
    const [lines, setLines] = useState([blankLine()]);
    const [previewDate, setPreviewDate] = useState(studioToday);
    const [preview, setPreview] = useState(null);

    const versions = useRateCardVersions(companyCode);
    const fields = useMatcherFields(companyCode);
    const save = useSaveRateCard(companyCode);
    const remove = useDeleteRateCard(companyCode);
    const previewMutation = usePreviewRateCard(companyCode);

    const visible = useMemo(() => {
        const query = search.trim().toLowerCase();
        return (cards || []).filter(card => {
            if (!query) return true;
            return (
                String(card.companyCode).toLowerCase().includes(query) ||
                String(card.companyName || '').toLowerCase().includes(query)
            );
        });
    }, [cards, search]);

    const selected = (cards || []).find(card => card.companyCode === companyCode);
    const fieldOptions = (fields.data?.fields || []).map(field => ({ value: field.field, label: field.field }));

    const startNew = version => {
        setEditing(true);
        const tomorrow = dayjs(studioToday).add(1, 'day').format('YYYY-MM-DD');
        setEffectiveFrom(tomorrow);
        setAllowBackdate(false);
        setLogisticsPct(version?.logisticsPct || 0);
        setScope(version?.scope === 'sku' ? 'sku' : 'bay');
        setUnmatchedRate(version?.unmatchedRate || 0);
        setNote(version?.note || '');
        setLines(version?.lines?.length ? cloneLines(version.lines) : [blankLine()]);
        setPreview(null);
    };

    const updateLine = (index, patch) => {
        setLines(current => current.map((line, lineIndex) => (lineIndex === index ? { ...line, ...patch } : line)));
    };

    const updateCondition = (lineIndex, conditionIndex, patch) => {
        setLines(current =>
            current.map((line, index) => {
                if (index !== lineIndex) return line;
                const conditions = line.conditions.map((condition, inner) =>
                    inner === conditionIndex ? { ...condition, ...patch } : condition
                );
                return { ...line, conditions };
            })
        );
    };

    const onSave = () => {
        const body = {
            effectiveFrom,
            logisticsPct: Number(logisticsPct) || 0,
            note,
            allowBackdate,
            scope,
            unmatchedRate: Number(unmatchedRate) || 0,
            lines: linesForApi(lines),
        };
        const run = () =>
            save.mutate(body, {
                onSuccess: () => {
                    message.success(allowBackdate ? 'Version saved and history repriced' : 'Version saved');
                    setEditing(false);
                },
                onError: error => message.error(errorText(error)),
            });
        if (allowBackdate && effectiveFrom < studioToday) {
            Modal.confirm({
                title: 'Reprice history?',
                content: `This replaces calculated revenue from ${effectiveFrom} through today.`,
                onOk: run,
            });
            return;
        }
        run();
    };

    const onPreview = () => {
        previewMutation.mutate(
            { date: previewDate, logisticsPct: Number(logisticsPct) || 0, scope, unmatchedRate: Number(unmatchedRate) || 0, lines: linesForApi(lines) },
            {
                onSuccess: response => setPreview(response?.data?.data || null),
                onError: error => message.error(errorText(error)),
            }
        );
    };

    return (
        <div style={{ padding: 16 }}>
            <Title level={3}>Rate cards</Title>
            <Text type="secondary">
                One rate per line. Best Secret, BST3, Boozt and Hirmer are priced once per SKU from the angle preset, on the first bay that finishes. Other clients stay one rate per bay type.
            </Text>
            {error && <Alert style={{ marginTop: 12 }} type="error" message={errorText(error)} />}
            <Row gutter={16} style={{ marginTop: 16 }}>
                <Col span={7}>
                    <Input placeholder="Search client" value={search} onChange={event => setSearch(event.target.value)} />
                    <Table
                        style={{ marginTop: 8 }}
                        size="small"
                        loading={isFetching}
                        rowKey="companyCode"
                        pagination={{ pageSize: 12, size: 'small' }}
                        dataSource={visible}
                        onRow={record => ({ onClick: () => { setCompanyCode(record.companyCode); setEditing(false); setPreview(null); } })}
                        columns={[
                            { title: 'Client', dataIndex: 'companyCode', render: (code, row) => `${code} ${row.companyName && row.companyName !== code ? row.companyName : ''}` },
                            { title: 'From', render: (_, row) => row.current?.effectiveFrom || '—' },
                        ]}
                    />
                </Col>
                <Col span={17}>
                    {!companyCode && <Alert type="info" message="Select a client." />}
                    {companyCode && (
                        <Space direction="vertical" style={{ width: '100%' }} size="middle">
                            <Card
                                title={`${selected?.companyName || companyCode} (${companyCode})`}
                                extra={<Button type="primary" onClick={() => startNew(selected?.current)}>New version</Button>}
                            >
                                {selected?.missingCompany && <Alert type="warning" message="No company record uses this code." />}
                                <div>
                                    Current from {selected?.current?.effectiveFrom || '—'} · logistics {selected?.current?.logisticsPct || 0}%
                                    {selected?.current?.scope === 'sku' ? ' · once per SKU' : ''}
                                    {selected?.current?.unmatchedRate ? ` · unmatched €${Number(selected.current.unmatchedRate).toFixed(2)}` : ''}
                                </div>
                                {selected?.next && <div>Scheduled {selected.next.effectiveFrom}</div>}
                                <Table
                                    style={{ marginTop: 8 }}
                                    size="small"
                                    rowKey="lineId"
                                    pagination={false}
                                    dataSource={selected?.current?.lines || []}
                                    columns={[
                                        { title: 'Label', dataIndex: 'label' },
                                        { title: 'Bay', render: (_, row) => (selected?.current?.scope === 'sku' ? 'article' : row.bayType) },
                                        {
                                            title: 'Rate',
                                            render: (_, row) => (row.band ? `${row.rate} (${bandText(row.band)})` : row.rate),
                                        },
                                        {
                                            title: 'When',
                                            render: (_, row) =>
                                                (row.conditions || []).map(condition => `${condition.field} ${condition.op} ${valueForForm(condition.value)}`).join(' and ') || 'any',
                                        },
                                        { title: '', dataIndex: 'note', render: value => (value ? <Tag color="orange">{value}</Tag> : null) },
                                    ]}
                                />
                            </Card>
                            <Card title="Versions" loading={versions.isFetching}>
                                <Table
                                    size="small"
                                    rowKey="_id"
                                    pagination={false}
                                    dataSource={versions.data || []}
                                    columns={[
                                        { title: 'From', dataIndex: 'effectiveFrom' },
                                        { title: 'Lines', render: (_, row) => (row.lines || []).length },
                                        { title: 'Logistics %', dataIndex: 'logisticsPct' },
                                        { title: 'Note', dataIndex: 'note', ellipsis: true },
                                        {
                                            title: '',
                                            render: (_, row) =>
                                                row.effectiveFrom > studioToday ? (
                                                    <Button
                                                        size="small"
                                                        danger
                                                        onClick={() =>
                                                            Modal.confirm({
                                                                title: `Delete version ${row.effectiveFrom}?`,
                                                                onOk: () => remove.mutate(row._id, { onError: error => message.error(errorText(error)) }),
                                                            })
                                                        }
                                                    >
                                                        Delete
                                                    </Button>
                                                ) : null,
                                        },
                                    ]}
                                />
                            </Card>
                            {editing && (
                                <Card title={`New version for ${companyCode}`}>
                                    <Space wrap>
                                        <DatePicker
                                            value={effectiveFrom ? dayjs(effectiveFrom) : null}
                                            onChange={value => setEffectiveFrom(value ? value.format('YYYY-MM-DD') : '')}
                                        />
                                        <InputNumber min={0} max={100} value={logisticsPct} onChange={setLogisticsPct} addonAfter="%" />
                                        <Select
                                            value={scope}
                                            style={{ width: 160 }}
                                            options={[
                                                { value: 'bay', label: 'Per bay type' },
                                                { value: 'sku', label: 'Once per SKU' },
                                            ]}
                                            onChange={setScope}
                                        />
                                        <InputNumber min={0} value={unmatchedRate} onChange={setUnmatchedRate} addonBefore="Unmatched €" />
                                        <Checkbox checked={allowBackdate} onChange={event => setAllowBackdate(event.target.checked)}>
                                            Allow past date and reprice
                                        </Checkbox>
                                    </Space>
                                    <Input style={{ marginTop: 8 }} placeholder="Note" value={note} onChange={event => setNote(event.target.value)} />
                                    {lines.map((line, index) => (
                                        <Card key={index} size="small" style={{ marginTop: 8 }} title={`Line ${index + 1}`}>
                                            <Space wrap>
                                                <Input placeholder="Label" value={line.label} onChange={event => updateLine(index, { label: event.target.value })} />
                                                <Select
                                                    value={line.bayType}
                                                    options={BAY_TYPES.map(value => ({ value, label: value }))}
                                                    onChange={bayType => updateLine(index, { bayType })}
                                                    style={{ width: 140 }}
                                                />
                                                <InputNumber min={0} value={line.rate} onChange={rate => updateLine(index, { rate })} />
                                                <Button onClick={() => updateLine(index, { conditions: [...line.conditions, blankCondition()] })}>Add condition</Button>
                                                <Button danger onClick={() => setLines(current => current.filter((_, lineIndex) => lineIndex !== index))}>Remove</Button>
                                            </Space>
                                            {line.conditions.map((condition, conditionIndex) => (
                                                <Space key={conditionIndex} wrap style={{ marginTop: 8 }}>
                                                    <Select
                                                        showSearch
                                                        style={{ width: 260 }}
                                                        value={condition.field || undefined}
                                                        options={fieldOptions}
                                                        placeholder="Field"
                                                        onChange={field => updateCondition(index, conditionIndex, { field })}
                                                    />
                                                    <Select
                                                        style={{ width: 120 }}
                                                        value={condition.op}
                                                        options={OPS.map(value => ({ value, label: value }))}
                                                        onChange={op => updateCondition(index, conditionIndex, { op })}
                                                    />
                                                    {condition.op !== 'exists' && condition.op !== 'notExists' && (
                                                        <Input
                                                            style={{ width: 220 }}
                                                            placeholder={condition.op === 'in' ? 'a, b, c' : 'value'}
                                                            value={condition.value}
                                                            onChange={event => updateCondition(index, conditionIndex, { value: event.target.value })}
                                                        />
                                                    )}
                                                    <Button
                                                        size="small"
                                                        onClick={() =>
                                                            updateLine(index, {
                                                                conditions: line.conditions.filter((_, inner) => inner !== conditionIndex),
                                                            })
                                                        }
                                                    >
                                                        ×
                                                    </Button>
                                                </Space>
                                            ))}
                                        </Card>
                                    ))}
                                    <Space style={{ marginTop: 12 }} wrap>
                                        <Button onClick={() => setLines(current => [...current, blankLine()])}>Add line</Button>
                                        <DatePicker
                                            value={previewDate ? dayjs(previewDate) : null}
                                            onChange={value => setPreviewDate(value ? value.format('YYYY-MM-DD') : '')}
                                        />
                                        <Button onClick={onPreview} loading={previewMutation.isLoading}>Preview</Button>
                                        <Button type="primary" onClick={onSave} loading={save.isLoading}>Save version</Button>
                                        <Button onClick={() => setEditing(false)}>Cancel</Button>
                                    </Space>
                                    {preview && (
                                        <Alert
                                            style={{ marginTop: 12 }}
                                            type={preview.unmatchedCount ? 'warning' : 'success'}
                                            message={`${preview.skuCount} SKUs on ${preview.date}. Gross EUR ${preview.gross}, net EUR ${preview.net}. Unmatched ${preview.unmatchedCount}.`}
                                        />
                                    )}
                                    {preview?.byLine?.length > 0 && (
                                        <Table
                                            style={{ marginTop: 8 }}
                                            size="small"
                                            rowKey={row => row.lineId || row.label}
                                            pagination={false}
                                            dataSource={preview.byLine}
                                            columns={[
                                                { title: 'Line', dataIndex: 'label' },
                                                { title: 'Count', dataIndex: 'count' },
                                                { title: 'Net', dataIndex: 'net' },
                                            ]}
                                        />
                                    )}
                                </Card>
                            )}
                        </Space>
                    )}
                </Col>
            </Row>
        </div>
    );
}
